import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Lock, Maximize2 } from 'lucide-react';
import { useLang } from '../../contexts/LanguageContext';
import { KANRIVA_SCREENSHOTS } from './constants';
import { ScreenshotLightbox, demoNoteFor } from './ScreenshotLightbox';

// K.A.N.R.I. teaser. Names the five moves and the loop, and nothing
// more — the operating detail (response ladder, navigation route, board
// architecture, rollout, maturity check) stays behind the request form
// on /method so the method remains the reason to talk to us.
const LETTER_TONES = [
  { ring: 'hover:border-[#00CED1]/60', glow: 'group-hover:text-[#00CED1]' },
  { ring: 'hover:border-[#4ADE80]/60', glow: 'group-hover:text-[#4ADE80]' },
  { ring: 'hover:border-[#FFB800]/60', glow: 'group-hover:text-[#FFB800]' },
  { ring: 'hover:border-[#F87171]/60', glow: 'group-hover:text-[#F87171]' },
  { ring: 'hover:border-[#A78BFA]/60', glow: 'group-hover:text-[#A78BFA]' },
];

export const MethodSection = () => {
  const { t, lang } = useLang('method');
  const moves = t.moves || [];
  const [zoom, setZoom] = useState(null);
  const demoNote = demoNoteFor(lang);
  // The four KANRI shots sit at the head of the platform screenshot list.
  const proofShots = KANRIVA_SCREENSHOTS.slice(0, 4);

  return (
    <section id="method" className="relative py-24 px-4 sm:px-6 lg:px-8 bg-[#0a0a0a] border-y border-white/5 overflow-hidden">
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 left-1/2 h-72 w-[52rem] -translate-x-1/2 rounded-full blur-3xl opacity-[0.14]"
        style={{ background: 'radial-gradient(circle, #00CED1 0%, transparent 70%)' }}
      />
      <div className="max-w-6xl mx-auto relative z-10">
        <div className="max-w-3xl">
          <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[#00CED1]">{t.eyebrow}</span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-white">{t.title}</h2>
          <p className="mt-4 text-base text-white/55 leading-relaxed">{t.lead}</p>
        </div>

        {/* Five letters. Name only — the one-liner appears on hover. */}
        <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="method-moves">
          {moves.map((m, i) => {
            const tone = LETTER_TONES[i] || LETTER_TONES[0];
            return (
              <div
                key={m.letter}
                tabIndex={0}
                className={`group relative rounded-2xl border border-white/10 bg-white/5 p-5 min-h-[136px] flex flex-col justify-between transition-colors focus:outline-none focus:border-[#00CED1]/60 ${tone.ring}`}
                data-testid={`method-move-${m.letter.toLowerCase()}`}
              >
                <span className={`font-display text-4xl font-bold text-white/25 transition-colors ${tone.glow}`}>{m.letter}</span>
                <div>
                  <h3 className="text-sm font-semibold uppercase tracking-[0.12em] text-white">{m.name}</h3>
                  <p
                    className="mt-1 text-[12px] leading-snug text-white/55 opacity-0 transition-opacity duration-300 group-hover:opacity-100 group-focus:opacity-100"
                    data-testid={`method-move-hint-${m.letter.toLowerCase()}`}
                  >
                    {m.hint}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-12 grid lg:grid-cols-[1.4fr_1fr] gap-6 items-start">
          <div className="rounded-2xl border border-white/10 bg-white/5 p-7">
            <p className="text-lg text-white/80 leading-relaxed font-medium">{t.pullQuote}</p>
            <p className="mt-4 text-sm text-white/45 leading-relaxed">{t.pullSub}</p>
            <p className="mt-6 text-xs font-semibold uppercase tracking-[0.22em] text-[#00CED1]">{t.tagline}</p>
          </div>

          <div className="rounded-2xl border border-dashed border-white/15 bg-white/5 p-7">
            <div className="flex items-center gap-2 text-white/40">
              <Lock className="h-3.5 w-3.5" />
              <span className="text-[10px] font-semibold uppercase tracking-[0.18em]">{t.lockedLabel}</span>
            </div>
            <ul className="mt-4 space-y-2" data-testid="method-locked-list">
              {(t.locked || []).map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-white/45">
                  <span className="mt-[7px] h-1 w-1 rounded-full bg-white/25 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <Link
              to="/method"
              className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-[#00CED1] px-5 py-2.5 text-sm font-semibold text-[#06211f] transition-all hover:gap-2.5 hover:brightness-110"
              data-testid="method-section-cta"
            >
              {t.cta} <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        {/* Proof — the method as working screens, from the demo tenant. */}
        <div className="mt-16" data-testid="method-proof">
          <div className="max-w-3xl">
            <h3 className="text-xl sm:text-2xl font-bold tracking-tight text-white" data-testid="method-proof-title">
              {t.proofTitle}
            </h3>
            <p className="mt-3 text-sm text-white/55 leading-relaxed">{t.proofLead}</p>
          </div>
          <div className="mt-7 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {proofShots.map((s, i) => {
              const caption = (t.proofShots || [])[i] || (lang === 'nl' ? s.labelNl : s.labelEn);
              return (
                <figure key={s.src} className="m-0 group relative">
                  <div
                    aria-hidden
                    className="pointer-events-none absolute -inset-2 rounded-2xl bg-gradient-to-br from-[#00CED1]/15 to-transparent blur-2xl"
                  />
                  <button
                    type="button"
                    onClick={() => setZoom({ src: s.src, caption })}
                    className="relative block w-full cursor-zoom-in rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00CED1]"
                    aria-label={`${caption} — ${demoNote}`}
                    data-testid={`method-proof-zoom-${i}`}
                  >
                    <img
                      src={s.src}
                      alt={`K.A.N.R.I. — ${caption} (demo data)`}
                      loading="lazy"
                      className="relative w-full rounded-xl border border-white/10 shadow-2xl transition-transform duration-500 group-hover:-translate-y-1"
                      data-testid={`method-proof-shot-${i}`}
                    />
                    <span className="pointer-events-none absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-md border border-white/20 bg-black/60 text-white/80 opacity-0 transition-opacity group-hover:opacity-100">
                      <Maximize2 className="h-3.5 w-3.5" />
                    </span>
                  </button>
                  <figcaption className="mt-2 text-center text-[11px] text-white/35">{caption}</figcaption>
                </figure>
              );
            })}
          </div>
          <p className="mt-4 text-center text-[11px] font-semibold uppercase tracking-[0.18em] text-white/45" data-testid="method-proof-demo-note">
            {demoNote}
          </p>
        </div>
      </div>

      <ScreenshotLightbox
        shot={zoom?.src}
        caption={zoom?.caption}
        note={demoNote}
        onClose={() => setZoom(null)}
      />
    </section>
  );
};
